import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Users,
  LogOut,
  TrendingUp,
  UserCheck,
  Clock,
  Menu,
  X,
  ChevronRight,
  Mail,
  Phone,
  Building,
  Calendar,
  Download,
  Search,
  MoreVertical,
  BarChart3,
  Bot,
  CalendarDays,
  Paintbrush,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sidebar Component
const Sidebar = ({ isOpen, setIsOpen }) => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { name: "Dashboard", path: "/admin/dashboard", icon: LayoutDashboard },
    { name: "Leads", path: "/admin/leads", icon: Users },
  ];

  const platformItems = [
    { name: "Analytics", path: "/platform", icon: BarChart3 },
    { name: "Generador IA", path: "/platform/ai", icon: Bot },
    { name: "CRM", path: "/platform/crm", icon: Users },
    { name: "Calendario", path: "/platform/calendar", icon: CalendarDays },
    { name: "Editor Visual", path: "/platform/editor", icon: Paintbrush },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
    toast.success("Sesión cerrada");
  };

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`fixed lg:relative inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] border-r border-white/5 flex flex-col transition-transform lg:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-white/5">
          <Link to="/admin/dashboard" className="flex items-center justify-center">
            <img 
              src="/logo-vibrant.png" 
              alt="klyvora7" 
              className="h-16 w-auto"
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 px-4 mb-2">Admin</div>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive
                    ? "bg-gradient-to-r from-[#2979FF]/20 to-[#00E5FF]/20 text-white border border-[#2979FF]/30"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}
                data-testid={`sidebar-link-${item.name.toLowerCase()}`}
              >
                <Icon className="w-5 h-5" />
                {item.name}
              </Link>
            );
          })}

          <div className="border-t border-white/5 my-3" />
          <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 px-4 mb-2">Plataforma</div>
          {platformItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-zinc-400 hover:text-white hover:bg-white/5"
                data-testid={`sidebar-link-platform-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon className="w-5 h-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-white/5">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-400 hover:text-red-400 hover:bg-red-500/10 w-full transition-all duration-200"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            Cerrar Sesión
          </button>
        </div>
      </aside>
    </>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        withCredentials: true,
      });
      setStats(response.data);
    } catch (error) {
      toast.error("Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-2 border-[#2979FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Leads",
      value: stats?.total_leads || 0,
      icon: Users,
      color: "from-blue-500 to-cyan-500",
    },
    {
      title: "Nuevos",
      value: stats?.leads_by_status?.nuevo || 0,
      icon: Clock,
      color: "from-amber-500 to-orange-500",
    },
    {
      title: "Convertidos",
      value: stats?.leads_by_status?.convertido || 0,
      icon: UserCheck,
      color: "from-emerald-500 to-green-500",
    },
    {
      title: "Tasa de Conversión",
      value: `${stats?.conversion_rate || 0}%`,
      icon: TrendingUp,
      color: "from-purple-500 to-pink-500",
    },
  ];

  const statusColors = {
    nuevo: "bg-amber-500",
    contactado: "bg-blue-500",
    convertido: "bg-emerald-500",
    perdido: "bg-red-500",
  };

  const serviceNames = {
    web: "Desarrollo Web",
    mobile: "Apps Móviles",
    saas: "Sistemas SaaS",
    design: "Diseño Gráfico",
    marketing: "Marketing Digital",
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-zinc-400">Resumen de leads y conversiones</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6"
              data-testid={`stat-card-${card.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} bg-opacity-20 flex items-center justify-center`}
                >
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <p className="text-zinc-400 text-sm mb-1">{card.title}</p>
              <p className="text-3xl font-bold text-white">{card.value}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads by Status */}
        <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-6">Leads por Estado</h3>
          <div className="space-y-4">
            {Object.entries(stats?.leads_by_status || {}).map(([status, count]) => {
              const total = stats?.total_leads || 1;
              const percentage = Math.round((count / total) * 100);
              return (
                <div key={status}>
                  <div className="flex justify-between mb-2">
                    <span className="text-zinc-400 capitalize">{status}</span>
                    <span className="text-white font-medium">{count}</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      className={`h-full ${statusColors[status]} rounded-full`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Leads by Service */}
        <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-6">Leads por Servicio</h3>
          <div className="space-y-4">
            {Object.entries(stats?.leads_by_service || {}).map(([service, count]) => {
              const total = stats?.total_leads || 1;
              const percentage = Math.round((count / total) * 100);
              return (
                <div key={service}>
                  <div className="flex justify-between mb-2">
                    <span className="text-zinc-400">{serviceNames[service] || service}</span>
                    <span className="text-white font-medium">{count}</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      className="h-full bg-gradient-to-r from-[#2979FF] to-[#00E5FF] rounded-full"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent Leads */}
      <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Leads Recientes</h3>
          <Link
            to="/admin/leads"
            className="text-[#00E5FF] text-sm hover:underline flex items-center gap-1"
          >
            Ver todos <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="space-y-4">
          {(stats?.recent_leads || []).map((lead, index) => (
            <div
              key={lead.id || index}
              className="flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/5"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2979FF] to-[#00E5FF] flex items-center justify-center text-white font-medium">
                  {lead.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div>
                  <p className="text-white font-medium">{lead.name}</p>
                  <p className="text-zinc-500 text-sm">{lead.email}</p>
                </div>
              </div>
              <div className="text-right">
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                    lead.status === "nuevo"
                      ? "bg-amber-500/20 text-amber-400"
                      : lead.status === "contactado"
                      ? "bg-blue-500/20 text-blue-400"
                      : lead.status === "convertido"
                      ? "bg-emerald-500/20 text-emerald-400"
                      : "bg-red-500/20 text-red-400"
                  }`}
                >
                  {lead.status}
                </span>
              </div>
            </div>
          ))}
          {(!stats?.recent_leads || stats.recent_leads.length === 0) && (
            <p className="text-zinc-500 text-center py-8">No hay leads aún</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Leads Page
const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterService, setFilterService] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [editNotes, setEditNotes] = useState("");

  useEffect(() => {
    fetchLeads();
  }, [filterStatus, filterService]);

  const fetchLeads = async () => {
    try {
      let url = `${API}/admin/leads`;
      const params = new URLSearchParams();
      if (filterStatus && filterStatus !== "all") params.append("status", filterStatus);
      if (filterService && filterService !== "all") params.append("service", filterService);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, { withCredentials: true });
      setLeads(response.data);
    } catch (error) {
      toast.error("Error al cargar leads");
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, status, notes = null) => {
    try {
      const data = { status };
      if (notes !== null) data.notes = notes;

      await axios.patch(`${API}/admin/leads/${leadId}`, data, {
        withCredentials: true,
      });

      toast.success("Lead actualizado");
      fetchLeads();
      setSelectedLead(null);
    } catch (error) {
      toast.error("Error al actualizar lead");
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("¿Estás seguro de eliminar este lead?")) return;

    try {
      await axios.delete(`${API}/admin/leads/${leadId}`, {
        withCredentials: true,
      });
      toast.success("Lead eliminado");
      fetchLeads();
    } catch (error) {
      toast.error("Error al eliminar lead");
    }
  };

  const exportLeads = async () => {
    try {
      const response = await axios.get(`${API}/admin/export`, {
        withCredentials: true,
      });

      const leads = response.data.leads;
      const headers = ["Nombre", "Email", "Teléfono", "Empresa", "Servicio", "Presupuesto", "Mensaje", "Estado", "Fecha"];
      const csvContent = [
        headers.join(","),
        ...leads.map((lead) =>
          [
            `"${lead.name || ""}"`,
            `"${lead.email || ""}"`,
            `"${lead.phone || ""}"`,
            `"${lead.company || ""}"`,
            `"${lead.service || ""}"`,
            `"${lead.budget || ""}"`,
            `"${(lead.message || "").replace(/"/g, '""')}"`,
            `"${lead.status || ""}"`,
            `"${lead.timestamp || ""}"`,
          ].join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `leads_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();

      toast.success("Leads exportados");
    } catch (error) {
      toast.error("Error al exportar leads");
    }
  };

  const filteredLeads = leads.filter(
    (lead) =>
      lead.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.company?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const serviceNames = {
    web: "Desarrollo Web",
    mobile: "Apps Móviles",
    saas: "Sistemas SaaS",
    design: "Diseño Gráfico",
    marketing: "Marketing Digital",
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-2 border-[#2979FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Leads</h1>
          <p className="text-zinc-400">{filteredLeads.length} leads encontrados</p>
        </div>
        <button
          onClick={exportLeads}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white hover:bg-white/10 transition-colors"
          data-testid="export-leads-button"
        >
          <Download className="w-4 h-4" />
          Exportar CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por nombre, email o empresa..."
            className="pl-12 bg-white/5 border-white/10 text-white placeholder:text-zinc-600"
            data-testid="search-leads-input"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-40 bg-white/5 border-white/10 text-white" data-testid="filter-status-select">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10">
            <SelectItem value="all" className="text-white">Todos</SelectItem>
            <SelectItem value="nuevo" className="text-white">Nuevo</SelectItem>
            <SelectItem value="contactado" className="text-white">Contactado</SelectItem>
            <SelectItem value="convertido" className="text-white">Convertido</SelectItem>
            <SelectItem value="perdido" className="text-white">Perdido</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterService} onValueChange={setFilterService}>
          <SelectTrigger className="w-full sm:w-48 bg-white/5 border-white/10 text-white" data-testid="filter-service-select">
            <SelectValue placeholder="Servicio" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10">
            <SelectItem value="all" className="text-white">Todos</SelectItem>
            <SelectItem value="web" className="text-white">Desarrollo Web</SelectItem>
            <SelectItem value="mobile" className="text-white">Apps Móviles</SelectItem>
            <SelectItem value="saas" className="text-white">Sistemas SaaS</SelectItem>
            <SelectItem value="design" className="text-white">Diseño Gráfico</SelectItem>
            <SelectItem value="marketing" className="text-white">Marketing Digital</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Leads Table */}
      <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left p-4 text-zinc-400 font-medium text-sm">Contacto</th>
                <th className="text-left p-4 text-zinc-400 font-medium text-sm hidden md:table-cell">Servicio</th>
                <th className="text-left p-4 text-zinc-400 font-medium text-sm hidden lg:table-cell">Presupuesto</th>
                <th className="text-left p-4 text-zinc-400 font-medium text-sm">Estado</th>
                <th className="text-left p-4 text-zinc-400 font-medium text-sm hidden sm:table-cell">Fecha</th>
                <th className="text-right p-4 text-zinc-400 font-medium text-sm">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((lead) => (
                <tr
                  key={lead.id}
                  className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                  data-testid={`lead-row-${lead.id}`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2979FF] to-[#00E5FF] flex items-center justify-center text-white font-medium flex-shrink-0">
                        {lead.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">{lead.name}</p>
                        <p className="text-zinc-500 text-sm truncate">{lead.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell">
                    <span className="text-zinc-300">{serviceNames[lead.service] || lead.service}</span>
                  </td>
                  <td className="p-4 hidden lg:table-cell">
                    <span className="text-zinc-300">{lead.budget || "-"}</span>
                  </td>
                  <td className="p-4">
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        lead.status === "nuevo"
                          ? "bg-amber-500/20 text-amber-400"
                          : lead.status === "contactado"
                          ? "bg-blue-500/20 text-blue-400"
                          : lead.status === "convertido"
                          ? "bg-emerald-500/20 text-emerald-400"
                          : "bg-red-500/20 text-red-400"
                      }`}
                    >
                      {lead.status}
                    </span>
                  </td>
                  <td className="p-4 hidden sm:table-cell">
                    <span className="text-zinc-500 text-sm">{formatDate(lead.timestamp)}</span>
                  </td>
                  <td className="p-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid={`lead-actions-${lead.id}`}>
                          <MoreVertical className="w-5 h-5 text-zinc-400" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#0A0A0A] border-white/10">
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedLead(lead);
                            setEditNotes(lead.notes || "");
                          }}
                          className="text-white hover:bg-white/10 cursor-pointer"
                        >
                          Ver detalles
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => updateLeadStatus(lead.id, "contactado")}
                          className="text-blue-400 hover:bg-blue-500/10 cursor-pointer"
                        >
                          Marcar contactado
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => updateLeadStatus(lead.id, "convertido")}
                          className="text-emerald-400 hover:bg-emerald-500/10 cursor-pointer"
                        >
                          Marcar convertido
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => updateLeadStatus(lead.id, "perdido")}
                          className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                        >
                          Marcar perdido
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => deleteLead(lead.id)}
                          className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                        >
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredLeads.length === 0 && (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-500">No hay leads que mostrar</p>
          </div>
        )}
      </div>

      {/* Lead Detail Modal */}
      <AnimatePresence>
        {selectedLead && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedLead(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0A0A0A] border border-white/10 rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              data-testid="lead-detail-modal"
            >
              <div className="p-6 border-b border-white/5 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">Detalles del Lead</h2>
                <button
                  onClick={() => setSelectedLead(null)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Contact Info */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2979FF] to-[#00E5FF] flex items-center justify-center text-white font-medium text-lg">
                      {selectedLead.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-lg">{selectedLead.name}</p>
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                          selectedLead.status === "nuevo"
                            ? "bg-amber-500/20 text-amber-400"
                            : selectedLead.status === "contactado"
                            ? "bg-blue-500/20 text-blue-400"
                            : selectedLead.status === "convertido"
                            ? "bg-emerald-500/20 text-emerald-400"
                            : "bg-red-500/20 text-red-400"
                        }`}
                      >
                        {selectedLead.status}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    <div className="flex items-center gap-3 text-zinc-400">
                      <Mail className="w-5 h-5" />
                      <a href={`mailto:${selectedLead.email}`} className="hover:text-white">
                        {selectedLead.email}
                      </a>
                    </div>
                    {selectedLead.phone && (
                      <div className="flex items-center gap-3 text-zinc-400">
                        <Phone className="w-5 h-5" />
                        <a href={`tel:${selectedLead.phone}`} className="hover:text-white">
                          {selectedLead.phone}
                        </a>
                      </div>
                    )}
                    {selectedLead.company && (
                      <div className="flex items-center gap-3 text-zinc-400">
                        <Building className="w-5 h-5" />
                        <span>{selectedLead.company}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-zinc-400">
                      <Calendar className="w-5 h-5" />
                      <span>{formatDate(selectedLead.timestamp)}</span>
                    </div>
                  </div>
                </div>

                {/* Service & Budget */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-white/5">
                    <p className="text-zinc-500 text-sm mb-1">Servicio</p>
                    <p className="text-white font-medium">
                      {serviceNames[selectedLead.service] || selectedLead.service}
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5">
                    <p className="text-zinc-500 text-sm mb-1">Presupuesto</p>
                    <p className="text-white font-medium">{selectedLead.budget || "No especificado"}</p>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <p className="text-zinc-500 text-sm mb-2">Mensaje</p>
                  <div className="p-4 rounded-xl bg-white/5">
                    <p className="text-zinc-300 whitespace-pre-wrap">{selectedLead.message}</p>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <p className="text-zinc-500 text-sm mb-2">Notas internas</p>
                  <Textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Agregar notas sobre este lead..."
                    rows={3}
                    className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600"
                  />
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => updateLeadStatus(selectedLead.id, "contactado", editNotes)}
                    className="flex-1 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/30 transition-colors"
                  >
                    Contactado
                  </button>
                  <button
                    onClick={() => updateLeadStatus(selectedLead.id, "convertido", editNotes)}
                    className="flex-1 px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-xl hover:bg-emerald-500/30 transition-colors"
                  >
                    Convertido
                  </button>
                  <button
                    onClick={() => updateLeadStatus(selectedLead.id, "perdido", editNotes)}
                    className="flex-1 px-4 py-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors"
                  >
                    Perdido
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Main Admin Dashboard
const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#030303] flex">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-[#030303]/80 backdrop-blur-xl border-b border-white/5 px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="mobile-menu-button"
            >
              <Menu className="w-6 h-6 text-white" />
            </button>

            <div className="hidden lg:block" />

            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-white font-medium">{user?.name || "Admin"}</p>
                <p className="text-zinc-500 text-sm">{user?.email}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2979FF] to-[#00E5FF] flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0)?.toUpperCase() || "A"}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-8">
          <Routes>
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="*" element={<DashboardPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
